import type { ZodValidator } from '@tanstack/zod-form-adapter';

import { validators } from '@connected/api-client/generated/validators.generated';
import { useElectricalProject } from '@connected/elektra-sdk/hooks/use-project';
import { Label } from '@connected/form-fields/components/label';
import { Button } from '@connected/ui/components/button';
import { Checkbox } from '@connected/ui/components/checkbox';
import { Input } from '@connected/ui/components/input';
import {
    Popout,
    PopoutBody,
    PopoutContent,
    PopoutFooter,
    PopoutHeader,
    PopoutTitle,
    PopoutTrigger,
} from '@connected/ui/components/popout';
import { useForm } from '@tanstack/react-form';
import { zodValidator } from '@tanstack/zod-form-adapter';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAvailableChecklists } from '@/hooks/queries/use-checklist';
import { useFormTouched } from '@/hooks/use-form-touched';
import { logger } from '@/lib/logger';
import { showConfirm } from '@/stores/confirmer';

import { useCreateChecklistItemMutation, useCreateDistributionBoardMutation } from '../hooks/use-create-distribution-board';

type DistributionBoardFormValues = {
    name: string;
    isPVInstallation: boolean;
};

export type DistributionBoardCreateModalProps = {
    electricalProjectVersionId: string;
    trigger?: React.ReactNode;
    onCreated: (distributionBoardId: string) => void;
};

export function DistributionBoardCreateModal({
    electricalProjectVersionId,
    trigger,
    onCreated,
}: DistributionBoardCreateModalProps) {
    const { t } = useTranslation('re-inspections');
    const { t: c } = useTranslation('common');

    const { hasInspectionKind, inspectionKindsArray } = useElectricalProject();
    const isOnlyScope12 = inspectionKindsArray.length === 1 && hasInspectionKind('Scope12');

    const [isOpen, setIsOpen] = useState(false);
    const [newDistributionBoardId, setNewDistributionBoardId] = useState<string>(() => crypto.randomUUID());

    useEffect(() => {
        if (!isOpen) {
            return;
        }
        // Reset the new distribution board ID when the modal is opened
        // This ensures that a new ID is generated each time the modal is opened
        const newId = crypto.randomUUID();
        logger.debug(['Resetting new distribution board ID', newId]);

        // eslint-disable-next-line react-hooks-extra/no-direct-set-state-in-use-effect
        setNewDistributionBoardId(newId);
    }, [isOpen]);

    const { data } = useAvailableChecklists({
        electricalProjectVersionId,
        kind: 'DistributionBoard',
    });

    const checklistItems = data.items?.map(item => ({
        ElectricalCheckListItemId: item.ElectricalCheckListItemId!,
        Description: item.Description!,
    })) ?? [];

    const createChecklistItemMutation = useCreateChecklistItemMutation({
        electricalProjectVersionId,
        checklistItems,
    });

    const createDistributionBoardMutation = useCreateDistributionBoardMutation({
        electricalProjectVersionId,
        onSuccess: (distributionBoardId: string) => {
            onCreated(distributionBoardId);
            setIsOpen(false);
        },
    });

    const defaultValues: DistributionBoardFormValues = {
        name: '',
        isPVInstallation: isOnlyScope12,
    };

    const { Field, handleSubmit, reset, store } = useForm<
        DistributionBoardFormValues,
        ZodValidator
    >({
        defaultValues,
        onSubmit: ({ value }) => {
            createDistributionBoardMutation.mutate({
                body: {
                    ElectricalProjectVersionId: electricalProjectVersionId,
                    Identifier: value.name,
                    ElectricalProjectDistributionBoardId: newDistributionBoardId,
                    SkipCreateCheckListItems: true,
                    IsPVInstallation: hasInspectionKind('Scope12') ? value.isPVInstallation : undefined,
                },
            });

            data.items?.forEach((checkList) => {
                createChecklistItemMutation.mutate({
                    body: {
                        ElectricalProjectVersionId: electricalProjectVersionId,
                        ObjectId: newDistributionBoardId,
                        ElectricalCheckListItemId: checkList.ElectricalCheckListItemId!,
                        ObjectKind: 'DistributionBoard',
                        ElectricalProjectCheckListItemId: crypto.randomUUID(),
                    },
                });
            });
        },
        validatorAdapter: zodValidator(),
    });

    const isFormTouched = useFormTouched(store, defaultValues);

    async function handleClose() {
        if (isFormTouched) {
            if (await showConfirm(c('messages.confirmClose'))) {
                setIsOpen(false);
                reset();
                createDistributionBoardMutation.reset();
            }
        }
        else {
            setIsOpen(false);
            reset();
            createDistributionBoardMutation.reset();
        }
    }

    async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
        event.preventDefault();
        await handleSubmit();
    }

    return (
        <Popout open={isOpen} onOpenChange={setIsOpen}>
            {trigger && (
                <PopoutTrigger asChild>
                    {trigger}
                </PopoutTrigger>
            )}
            <PopoutContent>
                <PopoutHeader>
                    <PopoutTitle>
                        {t('installation.distribution-boxes.modal.title.label')}
                    </PopoutTitle>
                </PopoutHeader>
                <PopoutBody>
                    <form
                        onSubmit={onSubmit}
                        className="flex flex-col gap-4 py-6"
                    >
                        <Field
                            name="name"
                            validators={{
                                onChange:
                                    validators.createElectricalProjectDistributionBoardCommand.identifier,
                            }}
                        >
                            {({ handleChange, state, name }) => (
                                <Label
                                    label={t(
                                        'installation.distribution-boxes.modal.form.name.label',
                                    )}
                                >
                                    <Input
                                        type="text"
                                        name={name}
                                        onChange={e =>
                                            handleChange(e.target.value)}
                                        value={state.value}
                                        placeholder={t(
                                            'installation.distribution-boxes.modal.form.name.placeholder',
                                        )}
                                    />
                                </Label>
                            )}
                        </Field>
                        {hasInspectionKind('Scope12') && !isOnlyScope12 && (
                            <Field name="isPVInstallation">
                                {({ handleChange, state }) => (
                                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                                        <Checkbox
                                            checked={state.value}
                                            onCheckedChange={checked => handleChange(checked === true)}
                                        />
                                        {t('distribution-box.information.form.isPVInstallation.label')}
                                    </label>
                                )}
                            </Field>
                        )}
                    </form>
                </PopoutBody>
                <PopoutFooter>
                    <Button
                        className="w-full"
                        onClick={handleClose}
                        variant="outline"
                        disabled={createDistributionBoardMutation.isPending}
                    >
                        {c('actions.general.cancel_short')}
                    </Button>
                    <Button
                        className="w-full"
                        onClick={handleSubmit}
                        disabled={createDistributionBoardMutation.isPending}
                    >
                        {c('actions.general.save')}
                    </Button>
                </PopoutFooter>
            </PopoutContent>
        </Popout>
    );
}
